import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as ReactLeaflet from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { PageBody } from '../../../elements/page-body';
import { useHttpClient } from '../../../shared/http-client';
import { useDrawer } from '../../../shared/drawer';
import { Customer, CustomerFormValues } from './types';
import { CustomerForm } from './customers-form';
import { CustomersList } from './customers-list';

// Import areas types and utilities
import { Area } from '../areas/types';
import { getAreaColor } from '../areas/color-utils';

export const CustomersMap = () => {
    const { t } = useTranslation() as { t: (key: string) => string };
    const navigate = useNavigate();
    const { api, state } = useHttpClient();
    const { open, close } = useDrawer();

    const [customers, setCustomers] = useState<Customer[]>([]);
    const [areas, setAreas] = useState<Area[]>([]);
    const [showAreas, setShowAreas] = useState<boolean>(true);
    const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [selectedArea, setSelectedArea] = useState<Area | null>(null);
    const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
    const [customersLoaded, setCustomersLoaded] = useState<boolean>(false);

    const MapContainerAny = (ReactLeaflet as any).MapContainer;
    const TileLayerAny = (ReactLeaflet as any).TileLayer;
    const MarkerAny = (ReactLeaflet as any).Marker;
    const CircleAny = (ReactLeaflet as any).Circle;
    const PopupAny = (ReactLeaflet as any).Popup;
    const useMapEvents = (ReactLeaflet as any).useMapEvents;

    const defaultCenter: [number, number] = [40.416775, -3.703790];

    // Custom icon for customers
    const customerIcon = L.divIcon({
        html: `
            <div style="
                background-color: #006bc6;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                border: 2px solid white;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            "></div>
        `,
        className: 'customer-marker',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
    });

    const acquiredCustomerIcon = L.divIcon({
        html: `
            <div style="
                background-color: #10b981;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                border: 2px solid white;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            "></div>
        `,
        className: 'customer-marker',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
    });

    useEffect(() => {
        loadCustomers();
    }, []);

    useEffect(() => {
        if (customersLoaded) {
            loadAreas();
        }
    }, [customersLoaded]);

    useEffect(() => {
        // Filter customers based on selected area
        if (selectedArea) {
            // Load customers for the selected area
            api(`/fleet/areas/${selectedArea.id}/customers/`, 'GET');
        } else {
            // Show all customers
            setFilteredCustomers(customers);
        }
    }, [selectedArea, customers]);

    useEffect(() => {
        // Handle API responses
        if (state.path?.includes('/fleet/customers/') && state.method === 'GET' && state.data) {
            if (Array.isArray(state.data.results)) {
                setCustomers(state.data.results);
                if (!selectedArea) {
                    setFilteredCustomers(state.data.results);
                }
                setCustomersLoaded(true);
            } else if (state.path?.includes('/fleet/areas/') && state.path?.includes('/customers/')) {
                // This is the response for customers in a specific area
                if (Array.isArray(state.data)) {
                    setFilteredCustomers(state.data);
                }
            }
        }

        if (state.path?.includes('/fleet/customers/') && (state.method === 'POST' || state.method === 'PATCH')) {
            if (state.data) {
                toast.success(t('common.message.saved'));
                loadCustomers();
                setIsCreating(false);
                setEditingCustomer(null);
                close();
            }
        }

        if (state.path?.includes('/fleet/customers/') && state.method === 'DELETE') {
            if (state.data || state.data === null) {
                toast.success(t('common.message.deleted'));
                loadCustomers();
            }
        }

        if (state.path?.includes('/fleet/areas/') && state.method === 'GET' && state.data) {
            if (Array.isArray(state.data.results)) {
                setAreas(state.data.results);
            }
        }

        if (state.error) {
            toast.error(t('common.message.error'));
        }
    }, [state, selectedArea]);

    const loadCustomers = () => {
        api('/fleet/customers/', 'GET');
    };

    const loadAreas = () => {
        api('/fleet/areas/', 'GET');
    };

    const handleMapClick = (lat: number, lng: number) => {
        if (isCreating) {
            const newCustomer: CustomerFormValues = {
                name: '',
                lat: lat,
                lon: lng,
                address: '',
                is_acquired: false
            };
            
            open(
                t('common.label.nuevo-customer'),
                <CustomerForm
                    item={newCustomer}
                    close={() => {
                        setIsCreating(false);
                        close();
                    }}
                    isEdit={false}
                />
            );
        }
    };

    const handleEdit = (customer: Customer) => {
        setEditingCustomer(customer);
        open(
            t('common.label.editar-customer'),
            <CustomerForm
                item={customer}
                close={() => {
                    setEditingCustomer(null);
                    close();
                }}
                isEdit={true}
            />
        );
    };

    const handleDelete = (id: number) => {
        if (window.confirm(t('common.modal.validate-delete'))) {
            api(`/fleet/customers/${id}/`, 'DELETE');
        }
    };

    const handleAreaClick = (area: Area) => {
        setSelectedArea(area.id === selectedArea?.id ? null : area);
    };

    const MapClickHandler = () => {
        useMapEvents({
            click: (e: any) => {
                handleMapClick(e.latlng.lat, e.latlng.lng);
            }
        });
        return null;
    };

    const displayCustomers = selectedArea ? filteredCustomers : customers;

    const goBackToTable = () => {
        navigate('/customers');
    };

    return (
        <PageBody 
            title={'common.label.customers'} 
            noMargin={true}
            secondaryAction={goBackToTable}
            secondaryActionTitle={'common.label.vista-tabla'}
        >
            <div className="w-full flex flex-col p-4" style={{ height: 'calc(100vh - 120px)' }}>
                {/* Header Controls */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4 flex-shrink-0">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setIsCreating(!isCreating)}
                                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                    isCreating
                                        ? 'bg-red-500 hover:bg-red-600 text-white'
                                        : 'bg-[#006bc6] hover:bg-[#0056a3] text-white'
                                }`}
                            >
                                {isCreating ? t('common.button.cancel') : t('common.label.nuevo-customer')}
                            </button>
                                                    </div>

                        <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="showAreas"
                                    checked={showAreas}
                                    onChange={(e) => setShowAreas(e.target.checked)}
                                    className="w-4 h-4 text-[#006bc6] border-gray-300 rounded focus:ring-[#006bc6]"
                                />
                                <label htmlFor="showAreas" className="text-sm font-medium text-gray-700 cursor-pointer">
                                    {t('common.label.mostrar-areas')}
                                </label>
                                <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-semibold">
                                    {areas.length} {t('common.label.areas')}
                                </span>
                                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                                    {displayCustomers.length} {t('common.label.customers')}
                                </span>
                            </div>
                    </div>

                    {/* Selected Area Info */}
                    {selectedArea && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: getAreaColor(selectedArea.id) }}
                                />
                                <span className="text-sm font-medium text-blue-800">
                                    {t('common.label.area-seleccionada')}: <strong>{selectedArea.name}</strong>
                                </span>
                                <span className="text-xs text-blue-600">
                                    ({filteredCustomers.length} {t('common.label.customers')})
                                </span>
                            </div>
                            <button
                                onClick={() => setSelectedArea(null)}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                                {t('common.button.mostrar-todos')}
                            </button>
                        </div>
                    )}

                    {isCreating && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm text-blue-800">
                                <span className="font-semibold">💡 {t('common.label.instrucciones')}:</span>{' '}
                                {t('common.label.click-mapa-crear-customer')}
                            </p>
                        </div>
                    )}
                </div>

                {/* Main Content */}
                <div className="flex-1 flex gap-4 min-h-0">
                    <>
                        {/* Map View */}
                        <div className="flex-1 rounded-lg overflow-hidden shadow-lg border border-gray-200">
                            <MapContainerAny
                                center={defaultCenter}
                                zoom={6}
                                style={{ height: '100%', width: '100%' }}
                                scrollWheelZoom={true}
                            >
                                <TileLayerAny
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />

                                {/* Areas */}
                                {showAreas &&
                                    areas.map((area) => (
                                        <CircleAny
                                            key={area.id}
                                            center={[area.center_lat, area.center_lon]}
                                            radius={area.radius * 1000}
                                            pathOptions={{
                                                color: getAreaColor(area.id),
                                                fillColor: getAreaColor(area.id),
                                                fillOpacity: selectedArea?.id === area.id ? 0.3 : 0.1,
                                                weight: selectedArea?.id === area.id ? 4 : 2,
                                                dashArray: selectedArea?.id === area.id ? undefined : '5, 5'
                                            }}
                                            eventHandlers={{
                                                click: () => handleAreaClick(area)
                                            }}
                                        />
                                    ))
                                }

                                {/* Customers */}
                                {displayCustomers.map((customer) => (
                                    customer.lat && customer.lon && (
                                        <MarkerAny
                                            key={customer.id}
                                            position={[customer.lat, customer.lon]}
                                            icon={customerIcon}
                                        >
                                            <PopupAny>
                                                <div className="p-2 min-w-[200px]">
                                                    <h3 className="font-bold text-lg mb-2" style={{ color: '#006bc6' }}>
                                                        {customer.name}
                                                    </h3>
                                                    {customer.address && (
                                                        <p className="text-sm text-gray-600 mb-2">{customer.address}</p>
                                                    )}
                                                    <p className="text-xs text-gray-500 mb-3">
                                                        {t('common.label.coordenadas')}: {customer.lat.toFixed(6)}, {customer.lon.toFixed(6)}
                                                    </p>
                                                    <div className="mb-3">
                                                        <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                                                            customer.is_acquired 
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-blue-100 text-blue-800'
                                                        }`}>
                                                            {customer.is_acquired 
                                                                ? t('common.form-label.es-cliente')
                                                                : t('common.label.no-adquirido')
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleEdit(customer)}
                                                            className="flex-1 px-3 py-1 bg-[#006bc6] text-white text-xs rounded hover:bg-[#0056a3] transition-all"
                                                        >
                                                            {t('common.label.edit')}
                                                        </button>
                                                        <button
                                                            onClick={() => customer.id && handleDelete(customer.id)}
                                                            className="flex-1 px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-all"
                                                        >
                                                            {t('common.button.delete')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </PopupAny>
                                        </MarkerAny>
                                    )
                                ))}

                                {isCreating && <MapClickHandler />}
                            </MapContainerAny>
                        </div>

                        {/* Sidebar List */}
                        <div className="w-80 flex-shrink-0">
                            <CustomersList 
                                customers={displayCustomers} 
                                onEdit={handleEdit} 
                                onDelete={handleDelete} 
                            />
                        </div>
                    </>
                </div>
            </div>
        </PageBody>
    );
};
